import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Layers,
    ChevronDown,
    ChevronRight,
    Plus,
    Trash2,
    RefreshCw,
} from 'lucide-react';
import {
    ProcessingPreset,
} from '../Processing/components/hooks/useProcessingPresets';
import { appendPresetSteps } from './utils/state/processingFlowSlice';

interface PresetPaletteProps {
    presets: ProcessingPreset[];
    isLoading: boolean;
    onRefresh: () => void;
    onDelete: (presetId: number) => void;
}

/**
 * Org preset palette: named, multi-step flow snippets synced from BoothLab and
 * booths. Click to append a preset's steps to the current flow.
 */
export default function PresetPalette({
    presets,
    isLoading,
    onRefresh,
    onDelete,
}: PresetPaletteProps) {
    const dispatch = useDispatch();
    const [isExpanded, setIsExpanded] = useState(true);

    return (
        <div className="border-b border-gray-200 bg-white">
            {/* Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-center space-x-2">
                    {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                    <Layers className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                        Presets
                    </h3>
                </div>
                <span className="text-xs text-gray-500">
                    {presets.length} synced
                </span>
            </button>

            {isExpanded && (
                <div
                    className="p-3 space-y-2 overflow-y-auto"
                    style={{ maxHeight: '30vh' }}
                >
                    {isLoading ? (
                        <p className="text-xs text-gray-500 text-center py-4">
                            Loading presets…
                        </p>
                    ) : presets.length === 0 ? (
                        <div className="text-center text-gray-500 py-4">
                            <p className="text-sm">No presets synced yet</p>
                            <p className="text-xs mt-1">
                                Save presets from BoothLab or with the button in
                                the toolbar.
                            </p>
                        </div>
                    ) : (
                        presets.map((preset) => (
                            <div
                                key={preset.id}
                                className="flex items-center p-3 bg-purple-50 rounded-lg border border-purple-200 hover:bg-purple-100 hover:border-purple-300 transition-colors group"
                                title={
                                    preset.description ||
                                    preset.steps
                                        .map((step) => step.name)
                                        .join(' → ')
                                }
                            >
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium text-gray-900 truncate">
                                        {preset.name}
                                    </h4>
                                    <p className="text-xs text-gray-500 truncate mt-1">
                                        {preset.steps.length} step
                                        {preset.steps.length === 1 ? '' : 's'}:{' '}
                                        {preset.steps
                                            .map((step) => step.name)
                                            .join(' → ')}
                                    </p>
                                </div>
                                <button
                                    onClick={() =>
                                        dispatch(
                                            appendPresetSteps({
                                                steps: preset.steps,
                                            }),
                                        )
                                    }
                                    className="flex-shrink-0 p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-200 rounded-md transition-colors"
                                    title="Add these steps to the flow"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => {
                                        if (
                                            window.confirm(
                                                `Delete preset "${preset.name}" for the whole org?`,
                                            )
                                        ) {
                                            onDelete(preset.id);
                                        }
                                    }}
                                    className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                                    title="Delete preset"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))
                    )}

                    <button
                        onClick={onRefresh}
                        className="w-full flex items-center justify-center space-x-1 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <RefreshCw className="w-3 h-3" />
                        <span>Refresh</span>
                    </button>
                </div>
            )}
        </div>
    );
}
